/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { Box, CircularProgress } from '@mui/material';

import { GraphType, ISimpleBarGraphProps } from '../Graphs/Graphs.Model';
import { FilterName, UserType } from '../../../Constants/App';
import { getAuthDetails } from '../../../Utilities/utils';

import BarGraph from '../Graphs/BarGraph/BarGraph';
import LineGraph from '../Graphs/LineGraph/LineGraph';
import MultiBarGraph from '../Graphs/BarGraph/MultiBarGraph';
import { NoDataFound } from '../NoDataFound/NoDataFound';
import DateRangeSelection from '../DateRangeSelection/DateRangeSelection';
import CustomDropDown from '../CustomDropDown/CustomDropdown';

import theme from '../../../Themes/theme';

import { Wrapper, HeaderWrapper, Title, GraphLoader } from './GraphContainer.styles';
import CustomDropDownAutoComplete from '../CustomDropDown/CustomDropdownAutoComplete';

/**
 * Difference between GraphContainer and GraphContainerV2 is only the data filtering based on date
 * @param props
 * @returns GraphContainerV2
 */

const GraphContainerV2 = (props: ISimpleBarGraphProps): JSX.Element => {
  const {
    graphData: { data, title, graphColor, xAxisLabel, yAxisLabel },
    type,
    loading,
    onDateRangeSelect,
    id,
    handleAssociateChanges,
    handleCustomerVendorChanges,
    handleLocationChanges,
    selectedAssociate,
    selectedCustomerVendor,
    defaultDateRangeOption,
    defaultOption,
    selectedFacility,
    isAssociateLoading,
    isCustomerVendorLoading,
    isFacilityLoading,
    labels,
    dateRangeOptions,
    filters,
    totalCounts,
    handlePaginationChanges,
    currentPages,
    searchString,
    setSearchString,
  } = props;

  const storedAuthDetails = getAuthDetails();
  const role = storedAuthDetails['custom:Role'] as UserType;

  const renderGraph = () => {
    if ((type as GraphType) === GraphType.BarGraph) {
      return (
        <BarGraph
          graphData={data}
          graphView={props.graphData?.graphView ?? 'vertical'}
          barColor={graphColor}
          yAxisLabel={title}
        />
      );
    } else if ((type as GraphType) === GraphType.LineGraph) {
      return <LineGraph graphData={data} LineColor={graphColor} />;
    } else if ((type as GraphType) === GraphType.MultiBarGraph) {
      return <MultiBarGraph graphData={data} yAxisLabel={yAxisLabel} labels={labels} xAxisLabel={xAxisLabel} />;
    }
    return null;
  };

  return (
    <>
      {Object.keys(props.graphData)?.length || loading ? (
        <Wrapper>
          <HeaderWrapper>
            <Title>{title}</Title>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1rem',
                width: '100%',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                paddingTop: '1rem',
              }}>
              <DateRangeSelection
                onDateRangeChange={(range) => {
                  onDateRangeSelect(range, id);
                }}
                defaultOption={defaultDateRangeOption}
                options={dateRangeOptions}
                isExcludePastDate={false}
              />
              {(type as GraphType) === GraphType.MultiBarGraph && (
                <>
                  {role === UserType.Customer && (
                    <CustomDropDown
                      title={filters.find((filter) => filter.name === FilterName.Location)?.title}
                      options={
                        filters.find((filter: { name: string }) => filter.name === FilterName.Location)?.options || []
                      }
                      selectedValue={selectedFacility}
                      onChange={handleLocationChanges}
                      isLoading={isFacilityLoading}
                      defaultOption={defaultOption}
                    />
                  )}
                  <CustomDropDown
                    title={filters.find((filter) => filter.name === FilterName.CustomerAndVendor)?.title}
                    options={
                      filters.find((filter: { name: string }) => filter.name === FilterName.CustomerAndVendor)
                        ?.options || []
                    }
                    selectedValue={selectedCustomerVendor}
                    onChange={handleCustomerVendorChanges}
                    isLoading={isCustomerVendorLoading}
                    defaultOption={defaultOption}
                  />
                  {role === UserType.Vendor && (
                    <CustomDropDownAutoComplete
                      title={filters.find((filter) => filter.name === FilterName.Associates)?.title}
                      options={
                        filters.find((filter: { name: string }) => filter.name === FilterName.Associates)?.options || []
                      }
                      selectedValue={selectedAssociate}
                      onChange={handleAssociateChanges}
                      isLoading={isAssociateLoading}
                      defaultOption={defaultOption}
                      totalCounts={totalCounts}
                      handlePaginationChanges={handlePaginationChanges}
                      currentPages={currentPages}
                      searchString={searchString}
                      setSearchString={setSearchString}
                      // dropDownWidth={'16rem'}
                    />
                  )}
                </>
              )}
            </Box>
          </HeaderWrapper>
          {loading ? (
            <GraphLoader>
              <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
            </GraphLoader>
          ) : data?.length ? (
            renderGraph()
          ) : (
            <NoDataFound />
          )}
        </Wrapper>
      ) : null}
    </>
  );
};

GraphContainerV2.defaultProps = {};

export default GraphContainerV2;
